import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import ReportsListItem from '../../../ui/listItems/ReportsListItem';
import { documentSVG, trash2SVG, editSVG } from '../../../lib/allCustomSVGs';
import get_presentations_api_call, {
  delete_presentation_api_call,
  get_presentation_shortcodes_api_call,
} from '../../../lib/api/presentations/presentations-api-call';
import { Presentation, ShortcodeItem } from '../../../interfaces/Presentation';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import Popup from '../../../ui/popup/Popup';
import ShortcodesPopup from './ShortcodesPopup';

type Props = {
  refreshTrigger?: number;
  frequencies?: { [key: string]: string } | null;
  onEditPresentation?: (presentation: Presentation) => void;
};

const ReportsPresentations = ({
  refreshTrigger,
  frequencies,
  onEditPresentation,
}: Props) => {
  const { t } = useTranslation();
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');
  const [deletingPresentationId, setDeletingPresentationId] = useState<
    string | null
  >(null);
  const [shortcodes, setShortcodes] = useState<ShortcodeItem[]>([]);
  const [isShortcodesPopupOpen, setIsShortcodesPopupOpen] = useState(false);
  const [shortcodesLoading, setShortcodesLoading] = useState(false);

  const fetchPresentations = async () => {
    setLoading('pending');
    try {
      const presentationsData = await get_presentations_api_call();

      if (presentationsData && presentationsData.length > 0) {
        setPresentations(presentationsData);
        setLoading('success');
      } else {
        setLoading('null');
      }
    } catch (error) {
      console.error('Error fetching presentations:', error);
      setLoading('error');
    }
  };

  const fetchShortcodes = async () => {
    try {
      const shortcodesData = await get_presentation_shortcodes_api_call();
      if (shortcodesData) {
        setShortcodes(shortcodesData);
      }
    } catch (error) {
      console.error('Error fetching shortcodes:', error);
    } finally {
      setShortcodesLoading(false);
    }
  };

  useEffect(() => {
    fetchPresentations();
  }, [refreshTrigger]);

  useEffect(() => {
    if (isShortcodesPopupOpen) {
      if (shortcodes.length === 0) {
        setShortcodesLoading(true);
        fetchShortcodes();
      } else {
        setShortcodesLoading(false);
      }
    } else {
      setShortcodesLoading(false);
    }
  }, [isShortcodesPopupOpen]);

  const handleEditPresentation = (presentation: Presentation) => {
    if (onEditPresentation) {
      onEditPresentation(presentation);
    }
  };

  const handleDeletePresentation = async (presentationId: string) => {
    setDeletingPresentationId(presentationId);
    try {
      const success = await delete_presentation_api_call(presentationId);
      if (success) {
        setPresentations((prev) => prev.filter((p) => p.id !== presentationId));
      }
    } catch (error) {
      console.error('Error deleting presentation:', error);
    } finally {
      setDeletingPresentationId(null);
    }
  };

  const getFrequencyText = (frequency: string) => {
    if (frequencies && frequencies[frequency]) {
      return frequencies[frequency];
    }
    return frequency.charAt(0).toUpperCase() + frequency.slice(1);
  };

  if (loading === 'pending') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <LoadingSpinner />
      </div>
    );
  }

  if (loading === 'error') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <NoData
          title={t('ErrorFetchingData')}
          subTitle={t('SomethingWentWrong-msg')}
        />
      </div>
    );
  }

  const toggleShortcodesPopup = () => {
    setIsShortcodesPopupOpen(!isShortcodesPopupOpen);
  };

  if (loading === 'null') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <SettingsCommonCard
          mainTitle={t('YourPresentations')}
          subTitle={t('AutomatedGoogleSlidesPresentations')}
          titleFontSize='2rem'
          titleMarginBottom='0.5rem'
          childrenStyles={{ marginTop: '2rem' }}
          onClick={toggleShortcodesPopup}
          btnText={t('Shortcodes')}
        >
          <NoData
            title={t('NoPresentationsFound')}
            subTitle={t('AddYourFirstPresentation')}
          />
        </SettingsCommonCard>
        <Popup
          styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
          setTrigger={toggleShortcodesPopup}
          trigger={isShortcodesPopupOpen}
        >
          <ShortcodesPopup
            shortcodes={shortcodes}
            loading={shortcodesLoading}
            onClose={toggleShortcodesPopup}
          />
        </Popup>
      </div>
    );
  }

  return (
    <div className='containerWithPaddingTopOnMobile'>
      <SettingsCommonCard
        mainTitle={t('YourPresentations')}
        subTitle={t('AutomatedGoogleSlidesPresentations')}
        titleFontSize='2rem'
        titleMarginBottom='0.5rem'
        childrenStyles={{
          marginTop: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
        onClick={toggleShortcodesPopup}
        btnText={t('Shortcodes')}
      >
        {presentations.map((presentation) => {
          const isDeleting = deletingPresentationId === presentation.id;
          return (
            <ReportsListItem
              key={presentation.id}
              id={presentation.id}
              icon={documentSVG}
              iconColor='#208df3'
              title={presentation.name}
              subTitle={`${presentation.description} • ${getFrequencyText(
                presentation.frequency
              )}`}
              onConfigClick={() => {
                if (!isDeleting) {
                  handleDeletePresentation(presentation.id);
                }
              }}
              configIcon={isDeleting ? null : trash2SVG}
              configIconColor='#FF4B4B'
              {...(isDeleting && { configLoading: true })}
              onEditClick={() => {
                if (!isDeleting) {
                  handleEditPresentation(presentation);
                }
              }}
              editIcon={editSVG}
              editIconColor='#6B7280'
              editIconSize='1.4rem'
            />
          );
        })}
      </SettingsCommonCard>
      <Popup
        styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
        setTrigger={toggleShortcodesPopup}
        trigger={isShortcodesPopupOpen}
      >
        <ShortcodesPopup
          shortcodes={shortcodes}
          loading={shortcodesLoading}
          onClose={toggleShortcodesPopup}
        />
      </Popup>
    </div>
  );
};

export default ReportsPresentations;
